import { useCallback, useMemo, useState } from "react";
import { useMe } from "../auth";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Compass, NotePencil, DotsThree, CaretRight, CaretLeft, Warning, ArrowsLeftRight, CalendarPlus, Barbell } from "@phosphor-icons/react";
import { useEntitlement } from "../useEntitlements";
import { Modal } from "../components/ui/Modal";
import { Card } from "../components/ui/Card";
import { SkeletonCard } from "../components/Skeleton";

import { useDashboard } from "../dashboardApi";
import { FABSpeedDial } from "../components/fab";
import { InlineError } from "../components/InlineError";
import { usePlan, usePlanPhases, useReschedule, useCopyPreviousWeek, type WorkoutItem } from "../planApi";
import { usePlanConflicts, usePmcSeries } from "../athleteMetricsApi";

import { WorkoutActionModal } from "../components/WorkoutActionModal";
import { PhaseStrip, PhaseSpec } from "../components/plan/PhaseStrip";
import { PlanWeekScrubber } from "../components/plan/PlanWeekScrubber";
import { PlanCoachUpdateAnchor } from "../components/plan/PlanCoachUpdateAnchor";
import {
  ConflictBanner,
  PlanConflict,
} from "../components/plan/ConflictBanner";
import { WeekView, PlanDayCard, PlanSessionCard } from "../components/plan/WeekView";
import { formatDistance, unitLabel, useUnitsPreference } from "../lib/useUnitsPreference";
import { WeekLoadHero, type WeekLoadHeroDay } from "../components/plan/WeekLoadHero";
import { weeklyLoadSums, weekTotals, itemLoad, itemDifficultyLevel } from "./planUtils";
import { PhaseStripOverlay, type PhaseSegment, type PeriodizationPhase } from "../components/canon/PhaseStripOverlay";

function phaseNameToCanonKey(name: string): PeriodizationPhase {
  const lc = name.toLowerCase();
  if (lc.includes("база") || lc.includes("base")) return "base";
  if (lc.includes("раз") || lc.includes("build")) return "build";
  if (lc.includes("пик") || lc.includes("peak")) return "peak";
  if (lc.includes("подв") || lc.includes("taper")) return "taper";
  if (lc.includes("гонк") || lc.includes("race")) return "race";
  return "build";
}

function buildPhaseSegments(
  specs: PhaseSpec[],
  todayISO: string,
): PhaseSegment[] {
  return specs.map((p) => {
    const [sy, sm, sd] = p.start.split("-").map(Number);
    const [ey, em, ed] = p.end.split("-").map(Number);
    const start = Date.UTC(sy, sm - 1, sd);
    const end = Date.UTC(ey, em - 1, ed);
    const today = Date.UTC(
      ...(todayISO.split("-").map(Number) as [number, number, number]),
    );
    const days = Math.max(1, Math.round((end - start) / 86_400_000));
    const active = today >= start && today <= end;
    return {
      phase: phaseNameToCanonKey(p.name),
      label: p.name,
      weight: days,
      active,
    };
  });
}

import { ListView } from "../components/plan/ListView";
import { MonthView, type MonthDayCell } from "../components/plan/MonthView";
import { PlanDaySheet } from "../components/plan/PlanDaySheet";
import {
  PlanLoadLandscape,
  type PlanLoadRange,
  type PlanLoadWeek,
} from "../components/plan/PlanLoadLandscape";
import { PlanViewToggle, type PlanViewMode } from "../components/plan/PlanViewToggle";
import { PlanWeekSidebar } from "../components/plan/PlanWeekSidebar";
import { AiSuggestion } from "../components/plan/AiSuggestion";
import { CycleView } from "../components/plan/CycleView";
import { SeasonView } from "../components/plan/SeasonView";
import {
  PLAN_SCALE_KEY,
  addDaysISO,
  buildCycleWeeks,
  isPlanScale,
  mondayISOOf,
  resolveCycleWindow,
  type PlanScale,
} from "./planScale";

/**
 * AthletePlanPage — WAVE 2.2 assembly at /plan-v2.
 *
 * Routed alongside the legacy /plan so we can A/B without losing
 * regressions. Once visual parity + feature coverage land we swap.
 *
 * Data wiring:
 *   - usePlan(from, to) for week sessions (existing endpoint)
 *   - usePlanPhases() for the macrocycle strip (new endpoint)
 *   - useDashboard().goal for the race date & total weeks
 *   - usePmcSeries(6) for chronic_load (used by the conflict detector)
 *   - usePlanConflicts({sessions, chronic_load, days_to_race})
 *     for the live conflict banner
 */

// W-FIX-H: дни недели из i18n (dashboard.weekdays_short ×4 локали),
// НЕ хардкод-русский. Fallback — на случай битой локали.
const WEEKDAY_FALLBACK = ["пн", "вт", "ср", "чт", "пт", "сб", "вс"];

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Phase 4 v150 — AthletePlanPage's weekday-from-jsDay grid logic (lines ~191,
// 241, 262, 340, 364) is Monday-first by construction. Wiring full first-day
// preference here would require restructuring multiple cross-day arrays and
// risks regression on the live /plan route. Deferred to follow-up sprint.
// /plan-legacy (PlanPage) already respects the pref. MonthCalendarGrid (used
// inside MonthInReviewPage) also already respects the pref.
function mondayOf(date: Date): Date {
  const day = date.getDay(); // 0=Sun..6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  const out = new Date(date);
  out.setDate(date.getDate() + diff);
  return out;
}

function isoFromDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function disciplineToSport(d: string | null | undefined): string {
  if (!d) return "other";
  const lc = d.toLowerCase();
  if (lc.startsWith("run")) return "run";
  if (lc.startsWith("swim")) return "swim";
  if (lc === "ride" || lc.startsWith("cycl") || lc === "bike") return "cycle";
  if (lc === "gravel") return "gravel";
  if (lc === "triathlon") return "triathlon";
  if (lc === "duathlon") return "duathlon";
  if (lc === "hyrox") return "hyrox";
  return "other";
}

function daysUntilRace(raceISO: string | null | undefined, todayISO: string): number | null {
  if (!raceISO) return null;
  const [ry, rm, rd] = raceISO.split("-").map(Number);
  const [ty, tm, td] = todayISO.split("-").map(Number);
  if (!ry || !ty) return null;
  const r = Date.UTC(ry, rm - 1, rd, 12);
  const t = Date.UTC(ty, tm - 1, td, 12);
  return Math.round((r - t) / 86_400_000);
}

export function AthletePlanPage() {
  const { t, i18n } = useTranslation("plan");
  const { t: tFab } = useTranslation("common");
  // Workout builder access is decided by the server (entitlements resolver:
  // tier + build mode + individual grants). Previously this line asked
  // `useAthleteEffectiveTier().isFree` — a client-side copy of the backend rule,
  // which stopped matching as soon as the rule gained layers: with build mode on
  // the API accepts the request while the UI still hides the button.
  const { allowed: canBuildWorkout } = useEntitlement("workout_builder");
  const { t: tDash } = useTranslation("dashboard");
  // W-REBUILD-6: solo-атлету доступен математический генератор плана.
  const { data: me } = useMe();
  const isSolo = me != null && me.has_coach !== true;
  // Пн-первые дни недели из i18n (dashboard.weekdays_short — Sunday-first)
  const weekdayLabels = useMemo(() => {
    const arr = tDash("weekdays_short", { returnObjects: true });
    if (!Array.isArray(arr) || arr.length !== 7) return WEEKDAY_FALLBACK;
    return Array.from({ length: 7 }, (_, i) => String(arr[(i + 1) % 7]));
  }, [tDash]);
  const nav = useNavigate();
  // Повторить прошлую неделю. Эндпоинт без входа — это функция, которой нет:
  // ровно тот урок, что оплатила v205 на конструкторе тренировок.
  const copyWeek = useCopyPreviousWeek();
  const [copyWeekNote, setCopyWeekNote] = useState<string | null>(null);
  // Phase 2.1c v149 — distance unit preference for plan session volume labels.
  const { unit } = useUnitsPreference();

  const today = useMemo(() => new Date(), []);
  const todayMonday = useMemo(() => mondayOf(today), [today]);
  // P0.2 P5 — week offset (0 = current, -1 = prev, +1 = next).
  const [weekOffset, setWeekOffset] = useState(0);
  const monday = useMemo(() => {
    const d = new Date(todayMonday);
    d.setDate(todayMonday.getDate() + weekOffset * 7);
    return d;
  }, [todayMonday, weekOffset]);
  const sunday = useMemo(() => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + 6);
    return d;
  }, [monday]);
  const fromISO = useMemo(() => isoFromDate(monday), [monday]);
  const toISO = useMemo(() => isoFromDate(sunday), [sunday]);
  const tISO = todayISO();

  const { data: plan, isLoading: planLoading, isError: planError, refetch: refetchPlan } = usePlan(fromISO, toISO);
  // W-REBUILD-2 — спарклайн 4 недель: подгружаем 3 предыдущие недели
  // одним запросом (react-query кэширует по диапазону).
  const sparkFromISO = useMemo(() => {
    const d = new Date(monday);
    d.setDate(monday.getDate() - 21);
    return isoFromDate(d);
  }, [monday]);
  const sparkToISO = useMemo(() => {
    const d = new Date(monday);
    d.setDate(monday.getDate() - 1);
    return isoFromDate(d);
  }, [monday]);
  const { data: prevPlan } = usePlan(sparkFromISO, sparkToISO);
  const { data: phasesResp } = usePlanPhases();
  const { data: dashboard } = useDashboard();
  const { data: pmcSeries } = usePmcSeries(6);

  // Convert weekday from ISO date → 0..6 Monday=0.
  const sessionsForDetector = useMemo(() => {
    if (!plan?.items) return [];
    return plan.items.map((w) => {
      const [y, m, d] = w.scheduled_date.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      const jsDay = date.getDay(); // 0=Sun..6=Sat
      const weekday = jsDay === 0 ? 6 : jsDay - 1;
      return {
        weekday,
        tss: Number(w.planned_load ?? 0),
        discipline: w.discipline,
        title: w.title,
      };
    });
  }, [plan]);

  // Chronic load from PMC series: last CTL * 7 (per-week TSS).
  const chronicLoad = useMemo(() => {
    const lastCtl = pmcSeries?.ctl?.[pmcSeries.ctl.length - 1];
    return lastCtl ? lastCtl * 7 : 0;
  }, [pmcSeries]);

  const raceDateISO = dashboard?.goal?.race_date ?? null;
  const days_to_race = daysUntilRace(raceDateISO, tISO);

  // Solo onboarding gap: a solo athlete with a race goal but no generated plan
  // lands on an empty week (the "build plan" action otherwise hides in the ⋯
  // overflow menu). Look across the whole [today → race] horizon — if there are
  // zero planned workouts anywhere, no plan exists yet and we surface a
  // prominent CTA. Coached athletes never self-generate, so this is solo-only.
  const { data: horizonPlan } = usePlan(tISO, raceDateISO ?? tISO, {
    enabled: isSolo && raceDateISO != null,
  });
  const soloHasNoPlan =
    isSolo &&
    raceDateISO != null &&
    horizonPlan != null &&
    horizonPlan.items.length === 0;

  const conflictBody = useMemo(
    () => ({
      sessions: sessionsForDetector,
      chronic_load: chronicLoad,
      days_to_race: days_to_race ?? undefined,
      lang: (i18n?.language ?? "ru").slice(0, 2),
    }),
    [sessionsForDetector, chronicLoad, days_to_race, i18n],
  );

  const { data: conflicts } = usePlanConflicts(conflictBody, sessionsForDetector.length > 0);

  // ── Масштабы: Неделя · Цикл · Сезон (ARCHITECTURE раздел 2) ────────────────
  // Масштаб запоминается между сессиями: кто планирует неделями, всегда
  // открывает неделю. week/month/list остаются ВИДАМИ недельного масштаба.
  const [scale, setScale] = useState<PlanScale>(() => {
    try {
      const saved = localStorage.getItem(PLAN_SCALE_KEY);
      if (isPlanScale(saved)) return saved;
    } catch {
      // silent: localStorage unavailable (private mode)
    }
    return "week";
  });
  const setScalePersisted = (s: PlanScale) => {
    setScale(s);
    try {
      localStorage.setItem(PLAN_SCALE_KEY, s);
    } catch {
      // silent: localStorage unavailable (private mode)
    }
  };

  // Project phasesResp into PhaseStrip's PhaseSpec
  const phaseSpecs: PhaseSpec[] = useMemo(
    () =>
      (phasesResp?.phases ?? []).map((p) => ({
        name: p.name,
        start: p.start_date,
        end: p.end_date,
      })),
    [phasesResp],
  );

  // Окно цикла: активная фаза целиком либо 6 недель вокруг текущей.
  // Данные тянутся только когда масштаб «Цикл» открыт.
  const cycleWindow = useMemo(() => resolveCycleWindow(phaseSpecs, tISO), [phaseSpecs, tISO]);
  const cycleToISO = useMemo(
    () => addDaysISO(cycleWindow.firstMondayISO, cycleWindow.weeks * 7 - 1),
    [cycleWindow],
  );
  const { data: cyclePlan } = usePlan(cycleWindow.firstMondayISO, cycleToISO, {
    enabled: scale === "cycle",
  });
  const { weeks: cycleWeeks, unit: cycleUnit } = useMemo(
    () =>
      buildCycleWeeks(
        cyclePlan?.items ?? [],
        phaseSpecs,
        cycleWindow.firstMondayISO,
        cycleWindow.weeks,
        tISO,
        raceDateISO,
      ),
    [cyclePlan, phaseSpecs, cycleWindow, tISO, raceDateISO],
  );
  // Касание недели в цикле опускает в «Неделю» на неё же — цикл не тупик.
  const onCycleWeekTap = (mondayISO: string) => {
    const todayMonday = mondayISOOf(tISO);
    const diffDays = Math.round(
      (Date.parse(mondayISO + "T00:00:00Z") - Date.parse(todayMonday + "T00:00:00Z")) / 86_400_000,
    );
    setWeekOffset(Math.round(diffDays / 7));
    setScalePersisted("week");
  };
  const cyclePhaseLabel = useMemo(() => {
    if (!cycleWindow.phaseName) return null;
    const key = phaseNameToCanonKey(cycleWindow.phaseName);
    return t(`plan_generate.phase_${key}`, {
      defaultValue: key === "race" ? "Гонка" : cycleWindow.phaseName,
    });
  }, [cycleWindow.phaseName, t]);

  // Build dayCards for WeekView
  const dayCards: PlanDayCard[] = useMemo(() => {
    const byDay: Record<number, PlanSessionCard[]> = {};
    for (let i = 0; i < 7; i++) byDay[i] = [];
    plan?.items.forEach((w) => {
      const [y, m, d] = w.scheduled_date.split("-").map(Number);
      const date = new Date(y, m - 1, d);
      const jsDay = date.getDay();
      const weekday = jsDay === 0 ? 6 : jsDay - 1;
      const isRace = raceDateISO === w.scheduled_date;
      // W-REBUILD-6: выполненная тренировка без planned-полей показывала
      // «Тренировка — — —» при живых actual_* в ответе API. Факт
      // приоритетнее плана для завершённых; титул-fallback — дисциплина.
      const isDone = w.status === "completed" || w.status === "done";
      const distanceKm = (isDone ? w.actual_distance_km : null) ?? w.planned_distance_km;
      const durationMin = (isDone ? w.actual_duration_minutes : null) ?? w.planned_duration_minutes;
      const kmLabel = unit === "mi" ? unitLabel("mi") : (t("workout.distance", { value: "", defaultValue: "км" }).replace(/[\d\s.]+/g, "").trim() || "км");
      byDay[weekday].push({
        id: w.id,
        time: null,
        sport: disciplineToSport(w.discipline),
        title:
          w.title ??
          t(`common:sport.${w.discipline}`, {
            defaultValue: t("workout_fallback", { defaultValue: "Тренировка" }),
          }),
        volumeLabel: distanceKm
          ? `${formatDistance(distanceKm, unit, 1)} ${kmLabel}`
          : durationMin
          ? `${durationMin} ${t("unit_min", { defaultValue: "мин" })}`
          : null,
        tss: (isDone ? w.actual_load : null) ?? w.planned_load ?? null,
        difficultyLevel: itemDifficultyLevel(w, dashboard?.ctl_current),
        zone: null,
        isRace,
        status: w.status as PlanSessionCard["status"],
      });
    });

    const todayDate = new Date();
    const todayJsDay = todayDate.getDay();
    const todayWeekday = todayJsDay === 0 ? 6 : todayJsDay - 1;
    const isCurrentWeek = isoFromDate(mondayOf(todayDate)) === fromISO;

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      return {
        weekday: i,
        weekdayLabel: weekdayLabels[i],
        date: d.getDate(),
        dateISO: isoFromDate(d),
        sessions: byDay[i] ?? [],
        isToday: isCurrentWeek && i === todayWeekday,
        isRace: raceDateISO === isoFromDate(d),
      };
    });
    // ctl_current — в списке обязательно: dashboard приходит отдельным
    // запросом и позже плана. Без него карточки застыли бы на уровне,
    // посчитанном без хронической нагрузки, то есть по запасной ветке.
  }, [plan, raceDateISO, monday, fromISO, t, unit, dashboard?.ctl_current]);

  // W-REBUILD-2 — данные hero: дни недели с items + недельные суммы TSS.
  const heroDays: WeekLoadHeroDay[] = useMemo(() => {
    const byISO: Record<string, WorkoutItem[]> = {};
    plan?.items.forEach((w) => {
      (byISO[w.scheduled_date] ??= []).push(w);
    });
    const todayIsoStr = isoFromDate(new Date());
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(monday.getDate() + i);
      const iso = isoFromDate(d);
      return {
        iso,
        label: weekdayLabels[i],
        dayNum: d.getDate(),
        items: byISO[iso] ?? [],
        isToday: iso === todayIsoStr,
        isRace: raceDateISO === iso,
      };
    });
  }, [plan, monday, weekdayLabels, raceDateISO]);

  const weeklySums = useMemo(() => {
    const prev = weeklyLoadSums(prevPlan?.items ?? [], sparkFromISO, 3);
    const current = weekTotals(plan?.items ?? []).tss;
    return [...prev, current];
  }, [prevPlan, plan, sparkFromISO]);

  // ── Развёрнутый профиль нагрузки ──────────────────────────────────
  //
  // Глубину истории спрашиваем ТОЛЬКО когда лист открыт: до этого запрос
  // на полгода плана никому не нужен, а он не дешёвый.
  const [loadOpen, setLoadOpen] = useState(false);
  const [loadRange, setLoadRange] = useState<PlanLoadRange>(16);
  const loadFromISO = useMemo(() => {
    const d = new Date(monday);
    d.setDate(monday.getDate() - 7 * (loadRange - 1));
    return isoFromDate(d);
  }, [monday, loadRange]);
  const loadToISO = useMemo(() => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + 6);
    return isoFromDate(d);
  }, [monday]);
  const { data: loadPlan, isFetching: loadFetching } = usePlan(
    loadFromISO, loadToISO, { enabled: loadOpen },
  );
  const loadWeeks: PlanLoadWeek[] = useMemo(() => {
    const sums = weeklyLoadSums(loadPlan?.items ?? [], loadFromISO, loadRange);
    const [fy, fm, fd] = loadFromISO.split("-").map(Number);
    return sums.map((tss, i) => {
      const d = new Date(Date.UTC(fy, fm - 1, fd));
      d.setUTCDate(d.getUTCDate() + i * 7);
      const iso = d.toISOString().slice(0, 10);
      const weekEnd = new Date(d);
      weekEnd.setUTCDate(weekEnd.getUTCDate() + 6);
      return {
        iso,
        label: `${String(d.getUTCDate()).padStart(2, "0")}.${String(d.getUTCMonth() + 1).padStart(2, "0")}`,
        tss,
        isCurrent: i === sums.length - 1,
        // Гонка помечается на НЕДЕЛЮ, а не на день: в масштабе недель
        // день не различим, а «в этой неделе старт» — различимо.
        isRace: raceDateISO != null
          && raceDateISO >= iso
          && raceDateISO <= weekEnd.toISOString().slice(0, 10),
      };
    });
  }, [loadPlan, loadFromISO, loadRange, raceDateISO]);

  const conflictsForBanner: PlanConflict[] = useMemo(
    () =>
      (conflicts ?? []).map((c) => ({
        key: c.key,
        severity: c.severity,
        title: c.title,
        summary: c.summary,
        affected_days: c.affected_days,
      })),
    [conflicts],
  );

  // P1.6-fix3: tap on a session opens the legacy action sheet
  // (complete / reschedule / copy / cancel). Look up the source
  // WorkoutItem by id — dayCards only carry the projected view-model.
  const [picked, setPicked] = useState<WorkoutItem | null>(null);
  // День, по которому нажали в календаре. Отдельно от `picked`: там
  // выбранная ТРЕНИРОВКА, здесь выбранная ДАТА, и у пустого дня второго
  // просто нет.
  const [daySheet, setDaySheet] = useState<MonthDayCell | null>(null);

  const onSessionClick = (s: PlanSessionCard) => {
    // W-REBUILD-5: month-режим показывает сессии вне текущей недели —
    // ищем и в месячном диапазоне, иначе tap был мёртвым.
    const wo =
      plan?.items.find((w) => w.id === s.id) ??
      monthPlan?.items.find((w) => w.id === s.id);
    if (wo) setPicked(wo);
  };

  // P0.2 P1+P6 — drag-and-drop reschedule, gated by edit mode (P6).
  const [editMode, setEditMode] = useState(false);
  // W-FIX-H — «⋯»-меню вторичных инструментов плана
  const [moreOpen, setMoreOpen] = useState(false);
  // W-REBUILD-2 — sheet «Сезон и риски» (фазы/AI/конфликты за одной строкой)
  const [contextOpen, setContextOpen] = useState(false);
  const activePhaseSpec = useMemo(
    () => phaseSpecs.find((p) => tISO >= p.start && tISO <= p.end) ?? null,
    [phaseSpecs, tISO],
  );
  const reschedule = useReschedule();
  const onSessionMove = (
    sessionId: number | string,
    _fromDateISO: string,
    toDateISO: string,
  ) => {
    if (typeof sessionId !== "number") return; // ids in WeekView are numeric workout ids
    reschedule.mutate({ id: sessionId, new_date: toDateISO });
  };

  // Cycle 18.10 — view mode (week / month / list). Default week to
  // match legacy. Persist via localStorage so the choice sticks across
  // sessions; URL sync can come later.
  const [viewMode, setViewMode] = useState<PlanViewMode>(() => {
    try {
      const saved = localStorage.getItem("plan.viewMode") as PlanViewMode | null;
      if (saved === "week" || saved === "month" || saved === "list") return saved;
    } catch {
      // silent: localStorage unavailable (private mode)
    }
    return "week";
  });
  const setViewModePersisted = (mode: PlanViewMode) => {
    setViewMode(mode);
    try {
      localStorage.setItem("plan.viewMode", mode);
    } catch {
      // silent: storage quota / private mode
    }
  };

  // Build month cells for MonthView. Anchors on the first day of the
  // focus month and pads with prev/next month spillover so the grid is
  // full Mon..Sun rows.
  // W-REBUILD-5 fix: сетка месяца питалась items ОДНОЙ недели (plan) —
  // 28 из 35 ячеек всегда были пусты, «месяц» не имел смысла. Теперь
  // month-режим тянет свой диапазон отдельным запросом.
  // W-REBUILD-6: фокус-месяц брался от понедельника недели — неделя
  // 29.06–05.07 показывала июнь вместо июля. ISO-конвенция: месяц
  // определяет четверг недели. Плюс: 35 ячеек резали 6-строчные месяцы.
  const monthFocusFirst = useMemo(() => {
    const thursday = new Date(monday);
    thursday.setDate(monday.getDate() + 3);
    return new Date(thursday.getFullYear(), thursday.getMonth(), 1);
  }, [monday]);
  const monthGridStart = useMemo(() => {
    const firstJsDay = monthFocusFirst.getDay();
    const firstWeekdayMonStart = firstJsDay === 0 ? 6 : firstJsDay - 1;
    const gridStart = new Date(monthFocusFirst);
    gridStart.setDate(monthFocusFirst.getDate() - firstWeekdayMonStart);
    return gridStart;
  }, [monthFocusFirst]);
  const monthCellCount = useMemo(() => {
    const firstJsDay = monthFocusFirst.getDay();
    const offset = firstJsDay === 0 ? 6 : firstJsDay - 1;
    const daysInMonth = new Date(
      monthFocusFirst.getFullYear(), monthFocusFirst.getMonth() + 1, 0,
    ).getDate();
    return Math.ceil((offset + daysInMonth) / 7) * 7;
  }, [monthFocusFirst]);
  const monthFromISO = useMemo(() => isoFromDate(monthGridStart), [monthGridStart]);
  const monthToISO = useMemo(() => {
    const d = new Date(monthGridStart);
    d.setDate(monthGridStart.getDate() + monthCellCount - 1);
    return isoFromDate(d);
  }, [monthGridStart, monthCellCount]);
  const { data: monthPlan } = usePlan(monthFromISO, monthToISO, {
    enabled: viewMode === "month",
  });

  const monthCells: MonthDayCell[] = useMemo(() => {
    const gridStart = monthGridStart;
    const sessionsByISO: Record<string, PlanSessionCard[]> = {};
    (monthPlan?.items ?? plan?.items ?? []).forEach((w) => {
      const list = (sessionsByISO[w.scheduled_date] ??= []);
      list.push({
        id: w.id,
        sport: disciplineToSport(w.discipline),
        title: w.title ?? "",
        // W-REBUILD-5: без tss ячейка месяца рендерила «—» даже при
        // сессиях (MonthView показывает тело только при tss > 0).
        tss: itemLoad(w),
        difficultyLevel: itemDifficultyLevel(w, dashboard?.ctl_current),
        status: w.status as PlanSessionCard["status"],
        isRace: raceDateISO === w.scheduled_date,
      });
    });

    const focusMonth = monthFocusFirst.getMonth();
    const todayISOStr = isoFromDate(today);

    return Array.from({ length: monthCellCount }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      const iso = isoFromDate(d);
      const jsDay = d.getDay();
      const weekday = jsDay === 0 ? 6 : jsDay - 1;
      return {
        weekday,
        date: d.getDate(),
        iso,
        inFocusMonth: d.getMonth() === focusMonth,
        sessions: sessionsByISO[iso] ?? [],
        isToday: iso === todayISOStr,
        isRace: raceDateISO === iso,
      };
    });
  }, [monthPlan, plan, raceDateISO, monthFocusFirst, monthCellCount, today, monthGridStart, dashboard?.ctl_current]);

  // Бар дня в сводке недели был КНОПКОЙ БЕЗ ОБРАБОТЧИКА: разметка кнопки,
  // курсор, фокус — и ничего по нажатию. Лист дня к этому моменту уже
  // написан, не хватало только проводки. Ячейку собираем той же
  // проекцией, что и месяц, — иначе один и тот же день открывался бы
  // двумя разными наборами полей.
  const openDayFromISO = useCallback(
    (iso: string) => {
      const [y, m, d] = iso.split("-").map(Number);
      const jsDay = new Date(y, m - 1, d).getDay();
      setDaySheet({
        weekday: jsDay === 0 ? 6 : jsDay - 1,
        date: d,
        iso,
        inFocusMonth: true,
        sessions: (plan?.items ?? [])
          .filter((w) => w.scheduled_date === iso)
          .map((w) => ({
            id: w.id,
            sport: disciplineToSport(w.discipline),
            title: w.title ?? "",
            tss: itemLoad(w),
            difficultyLevel: itemDifficultyLevel(w, dashboard?.ctl_current),
            status: w.status as PlanSessionCard["status"],
            isRace: raceDateISO === w.scheduled_date,
          })),
        isToday: iso === isoFromDate(new Date()),
        isRace: raceDateISO === iso,
      });
    },
    [plan, raceDateISO, dashboard?.ctl_current],
  );

  // Месячный итог для month-режима: раньше сверху висел только недельный
  // WeekLoadHero (281 TSS текущей недели) — при переключении на «месяц»
  // числа не отражали месяц вперёд. Считаем из monthCells (только focus-месяц).
  const monthSummary = useMemo(() => {
    let tss = 0;
    let count = 0;
    for (const c of monthCells) {
      if (!c.inFocusMonth) continue;
      for (const s of c.sessions) {
        // Плановые сессии без TSS (импортный план, load пуст) — тоже
        // тренировки месяца: счётчик считает все, сумма — что известно.
        count += 1;
        tss += s.tss ?? 0;
      }
    }
    return { tss, count };
  }, [monthCells]);

  if (planLoading) {
    return (
      <div
        data-testid="athlete-plan-loading"
        aria-live="polite"
        aria-busy="true"
        className="flex flex-col gap-3 px-3 py-4 max-w-[1480px] mx-auto"
      >
        <SkeletonCard className="h-10 w-2/3" />
        <SkeletonCard className="h-14" />
        <SkeletonCard className="h-48" />
        <SkeletonCard className="h-24" />
      </div>
    );
  }

  if (planError) {
    return (
      <div className="px-3 py-4 max-w-[1480px] mx-auto">
        <InlineError
          testId="athlete-plan-error"
          title={t("error.plan_title", { defaultValue: "Не удалось загрузить план" })}
          message={t("error.plan_message", {
            defaultValue: "Проверь соединение и попробуй ещё раз.",
          })}
          onRetry={() => refetchPlan()}
          retryLabel={t("error.retry", { defaultValue: "Повторить" })}
        />
      </div>
    );
  }

  const totalWeeksLabel = phasesResp?.phases?.length
    ? `${phasesResp.phases[phasesResp.phases.length - 1].end_week_index + 1} ${t("unit_weeks", { defaultValue: "нед" })}`
    : undefined;

  // Canon section ScreenPlanSolo (main.dc.html:834-880) — microcycle eyebrow
  // («МИКРОЦИКЛ · N / 16») + 28px Cyrillic period title above the
  // 16-week scrubber. The N derives from how many weeks elapsed in the
  // overall plan (first phase start → today), capped at the total.
  const totalMicrocycles = phasesResp?.phases?.length
    ? phasesResp.phases[phasesResp.phases.length - 1].end_week_index + 1
    : null;
  const currentMicrocycle = (() => {
    if (!phasesResp?.phases?.length || !totalMicrocycles) return null;
    const planStartISO = phasesResp.phases[0].start_date;
    const [py, pm, pd] = planStartISO.split("-").map(Number);
    const planStart = new Date(py, pm - 1, pd);
    const elapsedMs = monday.getTime() - planStart.getTime();
    const elapsedWeeks = Math.floor(elapsedMs / (7 * 86400_000));
    return Math.max(1, Math.min(totalMicrocycles, elapsedWeeks + 1));
  })();
  // W-FIX-H: месяцы через Intl по активной локали — было hardcoded «ЯНВАРЯ…»
  const periodTitle = (() => {
    const lang = (i18n?.language ?? "ru").slice(0, 2);
    const locale = lang === "uk" ? "uk-UA" : lang === "de" ? "de-DE" : lang === "en" ? "en-GB" : "ru-RU";
    const month = (d: Date) =>
      d.toLocaleDateString(locale, { month: "long", day: "numeric" })
        .replace(/^\d+\s*\.?\s*/, "")
        .toUpperCase();
    const sameMonth = monday.getMonth() === sunday.getMonth();
    if (sameMonth) {
      return `${monday.getDate()}–${sunday.getDate()} ${month(monday)}`;
    }
    return `${monday.getDate()} ${month(monday)} – ${sunday.getDate()} ${month(sunday)}`;
  })();

  return (
    <div data-testid="athlete-plan-page" className="flex flex-col gap-3 px-3 py-4 max-w-[1480px] mx-auto">
      {/* ── Масштаб: три высоты одного плана (ARCHITECTURE раздел 2) ── */}
      <div
        className="flex gap-1.5"
        role="group"
        aria-label={t("scale.aria", { defaultValue: "Масштаб плана" })}
        data-testid="plan-scale-switcher"
      >
        {(
          [
            { id: "week", label: t("scale.week", { defaultValue: "Неделя" }) },
            { id: "cycle", label: t("scale.cycle", { defaultValue: "Цикл" }) },
            { id: "season", label: t("scale.season", { defaultValue: "Сезон" }) },
          ] as { id: PlanScale; label: string }[]
        ).map((s) => {
          const active = scale === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setScalePersisted(s.id)}
              aria-pressed={active}
              data-testid={`plan-scale-${s.id}`}
              className={`flex-1 h-8 rounded-md text-[11.5px] font-mono uppercase tracking-[0.08em] transition-colors ${
                active
                  ? "bg-accent text-accent-on font-semibold"
                  : "bg-bg-card text-fg-muted shadow-[var(--shadow-card)]"
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* ── W-REBUILD-2: шапка = eyebrow + ‹ период › (недельная навигация
             живёт в заголовке — дубль H1/week-nav убит). Только «Неделя». ── */}
      {scale === "week" && (
      <div data-testid="plan-canon-period-header" className="px-1">
        {currentMicrocycle != null && totalMicrocycles != null && (
          <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
            {t("plan_canon.microcycle_eyebrow", {
              defaultValue: "МИКРОЦИКЛ · {{n}} / {{total}}",
              n: currentMicrocycle,
              total: totalMicrocycles,
            })}
          </div>
        )}
        <div className="mt-2 flex items-center gap-2">
          <button
            type="button"
            data-testid="plan-week-prev"
            onClick={() => setWeekOffset((w) => w - 1)}
            aria-label={t("week_nav.prev", { defaultValue: "Предыдущая неделя" })}
            className="w-9 h-9 shrink-0 rounded-md bg-bg-card text-fg-secondary hover:text-fg-primary transition-colors flex items-center justify-center"
          >
            <CaretLeft size={16} aria-hidden />
          </button>
          <h1 className="flex-1 min-w-0 text-center text-[21px] font-bold tracking-[-0.02em] leading-none text-fg-primary font-display truncate">
            {periodTitle}
          </h1>
          <button
            type="button"
            data-testid="plan-week-next"
            onClick={() => setWeekOffset((w) => w + 1)}
            aria-label={t("week_nav.next", { defaultValue: "Следующая неделя" })}
            className="w-9 h-9 shrink-0 rounded-md bg-bg-card text-fg-secondary hover:text-fg-primary transition-colors flex items-center justify-center"
          >
            <CaretRight size={16} aria-hidden />
          </button>
        </div>
        {weekOffset !== 0 && (
          <div className="mt-1.5 flex justify-center">
            <button
              type="button"
              data-testid="plan-week-today"
              onClick={() => setWeekOffset(0)}
              className="px-3 py-1 rounded-pill bg-accent/15 ring-1 ring-inset ring-accent/40 text-accent text-[10.5px] font-semibold hover:bg-accent/25 transition-colors"
            >
              {t("week_nav.current", { defaultValue: "Сегодня" })}
            </button>
          </div>
        )}
      </div>
      )}

      {/* Canon section ScreenPlanCoached — coach update anchor (Solo → null). */}
      <PlanCoachUpdateAnchor />

      {/* ── HERO: неделя как нагрузочный профиль ── */}
      {scale === "week" && (
        <WeekLoadHero
          days={heroDays}
          weeklySums={weeklySums}
          onDayClick={openDayFromISO}
          onExpand={() => setLoadOpen(true)}
        />
      )}

      {/* Solo без плана — заметный вход в генератор (иначе прячется в ⋯). */}
      {soloHasNoPlan && (
        <button
          type="button"
          data-testid="plan-empty-solo-cta"
          onClick={() => nav("/plan/generate")}
          className="indigo-card p-4 flex items-center gap-3 text-left w-full hover:bg-bg-raised active:bg-bg-raised transition-colors ring-1 ring-inset ring-accent/25"
        >
          <span className="w-10 h-10 rounded-full bg-accent/15 text-accent flex items-center justify-center shrink-0">
            <CalendarPlus size={20} aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-fg-primary">
              {t("plan_empty_solo.title", { defaultValue: "Плана пока нет" })}
            </span>
            <span className="block text-[12px] text-fg-secondary leading-snug">
              {t("plan_empty_solo.body", {
                defaultValue:
                  "Собери план под свою гонку — недели, фазы и нагрузка рассчитаются автоматически.",
              })}
            </span>
          </span>
          <span className="shrink-0 inline-flex items-center gap-1 text-[12px] font-semibold text-accent">
            {t("plan_generate.menu_cta", { defaultValue: "Собрать план под цель" })}
            <CaretRight size={14} aria-hidden />
          </span>
        </button>
      )}

      {/* ── Одна умная строка контекста: фаза · гонка · риски → sheet ──
             В «Сезоне» не нужна: фазы и гонка там и есть содержимое. ── */}
      {scale !== "season" && (phaseSpecs.length > 0 || days_to_race != null || conflictsForBanner.length > 0) && (
        <button
          type="button"
          data-testid="plan-context-line"
          onClick={() => setContextOpen(true)}
          className="indigo-card px-4 py-2.5 flex items-center gap-2.5 text-left hover:bg-bg-raised active:bg-bg-raised transition-colors"
        >
          {activePhaseSpec && (
            <span className="text-[10px] font-mono font-bold uppercase tracking-[0.16em] text-accent shrink-0">
              {/* имя фазы из БД (base/build/taper) — не сырым enum'ом */}
              {(() => {
                const key = phaseNameToCanonKey(activePhaseSpec.name);
                return t(`plan_generate.phase_${key}`, {
                  defaultValue: key === "race" ? "Гонка" : activePhaseSpec.name,
                });
              })()}
            </span>
          )}
          {days_to_race != null && days_to_race >= 0 && (
            <span className="text-[12px] text-fg-secondary truncate">
              {t("context.race_in", { defaultValue: "гонка через {{n}} дн", n: days_to_race })}
            </span>
          )}
          {conflictsForBanner.length > 0 && (
            <span
              data-testid="plan-context-risks"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-warn shrink-0"
            >
              <Warning size={12} aria-hidden />
              {conflictsForBanner.length}
            </span>
          )}
          <CaretRight size={14} className="text-fg-muted ml-auto shrink-0" aria-hidden />
        </button>
      )}

      <Modal
        isOpen={contextOpen}
        onClose={() => setContextOpen(false)}
        eyebrow={t("context.sheet_eyebrow", { defaultValue: "ПЛАН · КОНТЕКСТ" })}
        title={t("context.sheet_title", { defaultValue: "Сезон и риски" })}
        testId="plan-context-sheet"
      >
        <div className="p-3 space-y-3">
          {currentMicrocycle != null && phaseSpecs.length > 0 && (
            <PlanWeekScrubber
              phases={phaseSpecs}
              currentWeek={currentMicrocycle}
              raceDateISO={raceDateISO}
            />
          )}
          <PhaseStrip
            phases={phaseSpecs}
            todayISO={tISO}
            raceDateISO={raceDateISO}
            totalWeeksLabel={totalWeeksLabel}
          />
          <AiSuggestion />
          <ConflictBanner conflicts={conflictsForBanner} />
        </div>
      </Modal>

      {/* ── Chrome: только переключатель вида + «⋯» (влезает в 390px;
             РЕДАКТИРОВАТЬ переехал в «⋯» — мандат-развилка 2026-07-04).
             «⋯» — постоянная шапка слота (спека): Конструктор/Шаблоны/
             Генератор доступны из любого масштаба. ── */}
      <div className="flex items-center gap-2">
        {scale === "week" && <PlanViewToggle value={viewMode} onChange={setViewModePersisted} />}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          data-testid="plan-more-menu"
          aria-label={t("plan_more.aria", { defaultValue: "Ещё инструменты плана" })}
          className="ml-auto px-2.5 h-9 rounded-md text-fg-secondary hover:text-fg-primary bg-bg-card transition-colors"
        >
          <DotsThree size={18} weight="bold" aria-hidden />
        </button>
      </div>

      {copyWeekNote && (
        <Card
          variant="indigo"
          padding={0}
          data-testid="plan-copy-week-note"
          className="px-4 py-2.5 flex items-center gap-2.5 bg-accent/10"
        >
          <span className="flex-1 text-[12px] text-fg-primary">{copyWeekNote}</span>
          <button
            type="button"
            onClick={() => setCopyWeekNote(null)}
            className="text-[12px] text-fg-muted hover:text-fg-primary"
          >
            {t("plan_copy_week.dismiss", { defaultValue: "Понятно" })}
          </button>
        </Card>
      )}

      {editMode && scale === "week" && (
        <Card
          variant="indigo"
          padding={0}
          data-testid="plan-edit-banner"
          className="px-4 py-2.5 flex items-center gap-2.5 bg-accent/10"
        >
          <ArrowsLeftRight size={14} className="text-accent shrink-0" aria-hidden />
          <span className="flex-1 text-[12px] text-fg-primary">
            {t("plan_edit.banner", { defaultValue: "Режим переноса: перетащи тренировку на другой день." })}
          </span>
          <button
            type="button"
            data-testid="plan-edit-toggle"
            data-active="true"
            onClick={() => setEditMode(false)}
            className="px-3 py-1.5 rounded-md bg-accent text-accent-on text-[10px] font-mono font-bold uppercase tracking-[0.16em]"
          >
            {t("plan_edit.done", { defaultValue: "ГОТОВО" })}
          </button>
        </Card>
      )}

      <Modal
        isOpen={moreOpen}
        onClose={() => setMoreOpen(false)}
        eyebrow={t("plan_more.eyebrow", { defaultValue: "ПЛАН · ИНСТРУМЕНТЫ" })}
        title={t("plan_more.title", { defaultValue: "Ещё" })}
        testId="plan-more-sheet"
      >
        <div className="p-3 space-y-1">
          {/* W-REBUILD-2: режим переноса живёт в «⋯» — редкое действие */}
          <button
            type="button"
            data-testid="plan-edit-menu-item"
            onClick={() => {
              setMoreOpen(false);
              setEditMode(true);
            }}
            className="w-full flex items-center justify-between px-3 py-3 rounded-md text-sm text-fg-primary hover:bg-bg-raised active:bg-bg-raised transition-colors text-left"
          >
            {t("plan_edit.enter_menu", { defaultValue: "Перенести тренировки" })}
            <ArrowsLeftRight size={14} className="text-fg-muted" aria-hidden />
          </button>
          {(
            [
              ...(isSolo
                ? [{ to: "/plan/generate", testId: "plan-generate-link", label: t("plan_generate.menu_cta", { defaultValue: "Собрать план под цель" }) }]
                : []),
              { to: "/plan/history", testId: "plan-history-link", label: t("plan_history.cta", { defaultValue: "История плана" }) },
              { to: "/plan/templates", testId: "plan-templates-link", label: t("plan_templates.cta", { defaultValue: "Шаблоны" }) },
              { to: "/carb-timing", testId: "carb-timing-link", label: t("carb_timing.cta", { defaultValue: "Питание" }) },
              { to: "/travel-mode", testId: "travel-mode-link", label: t("travel_mode.cta", { defaultValue: "Travel-неделя" }) },
              { to: "/ow-spots", testId: "ow-spots-link", label: t("ow_spots.cta", { defaultValue: "Open-water споты" }) },
              // 2026-07-28 — протоколы жары и высоты того же класса, что
              // travel-неделя и open-water: подготовка тела к условиям
              // старта. Экраны были построены и не имели ни одного входа.
              { to: "/protocol/heat", testId: "protocol-heat-link", label: t("heat_protocol.cta", { defaultValue: "Акклиматизация к жаре" }) },
              { to: "/protocol/altitude", testId: "protocol-altitude-link", label: t("altitude_protocol.cta", { defaultValue: "Протокол высоты" }) },
            ] as { to: string; testId: string; label: string }[]
          ).map((item) => (
            <button
              key={item.to}
              type="button"
              data-testid={item.testId}
              onClick={() => {
                setMoreOpen(false);
                nav(item.to);
              }}
              className="w-full flex items-center justify-between px-3 py-3 rounded-md text-sm text-fg-primary hover:bg-bg-raised active:bg-bg-raised transition-colors text-left"
            >
              {item.label}
              <CaretRight size={14} className="text-fg-muted" aria-hidden />
            </button>
          ))}
        </div>
      </Modal>
      {/* ── Масштаб «Цикл»: недели столбиками, фазы, ключевые точки ── */}
      {scale === "cycle" && (
        <CycleView weeks={cycleWeeks} unit={cycleUnit} phaseLabel={cyclePhaseLabel} onWeekTap={onCycleWeekTap} />
      )}

      {/* ── Масштаб «Сезон»: от сегодня до цели ── */}
      {scale === "season" && (
        <SeasonView
          phases={phaseSpecs}
          todayISO={tISO}
          raceDateISO={raceDateISO}
          daysToRace={days_to_race}
          goal={dashboard?.goal ?? null}
          currentWeek={currentMicrocycle}
          totalWeeksLabel={totalWeeksLabel}
        />
      )}

      {scale === "week" && (
      <div className="flex flex-col lg:flex-row gap-3.5 items-stretch lg:items-start">
        <div className="flex-1 min-w-0 w-full">
          {viewMode === "week" && (
            <WeekView
              dayCards={dayCards}
              onSessionClick={onSessionClick}
              onSessionMove={editMode ? onSessionMove : undefined}
            />
          )}
          {viewMode === "list" && (
            <ListView dayCards={dayCards} onSessionClick={onSessionClick} />
          )}
          {viewMode === "month" && (
            <>
              {phaseSpecs.length > 0 && (
                <Card variant="indigo" padding={12} className="mb-3">
                  <PhaseStripOverlay
                    eyebrow={t("phase_block_eyebrow", { defaultValue: "ФАЗА БЛОКА" })}
                    segments={buildPhaseSegments(phaseSpecs, tISO)}
                  />
                </Card>
              )}
              {/* Канон-стат-пары (как WeekLoadHero раздел totals): число ink-1 17px +
                  9px uppercase-лейбл, без mono, «Цвет = данные» (без accent-chrome). */}
              <Card
                variant="indigo"
                padding={12}
                data-testid="plan-month-summary"
                className="mb-3 flex items-baseline gap-4 flex-wrap"
              >
                <span className="text-[9px] uppercase tracking-[0.22em] text-fg-muted font-bold self-center">
                  {t("month_summary.eyebrow", { defaultValue: "МЕСЯЦ" })}
                </span>
                <span className="flex items-baseline gap-1">
                  <span className="text-[17px] font-bold tabular-nums text-fg-primary leading-none">{monthSummary.tss}</span>
                  <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-bold">TSS</span>
                </span>
                <span className="flex items-baseline gap-1">
                  <span className="text-[17px] font-bold tabular-nums text-fg-primary leading-none">{monthSummary.count}</span>
                  <span className="text-[9px] uppercase tracking-[0.14em] text-fg-muted font-bold">
                    {t("month_summary.workouts_unit", { defaultValue: "трен." })}
                  </span>
                </span>
              </Card>
              <MonthView
                cells={monthCells}
                weekdayLabels={weekdayLabels}
                monthLabel={monthFocusFirst.toLocaleDateString(i18n?.language ?? "ru", {
                  month: "long",
                  year: "numeric",
                })}
                onDayClick={(c) => setDaySheet(c)}
              />
            </>
          )}
        </div>
        {/* W-REBUILD-2: на мобиле сводка живёт в WeekLoadHero — сайдбар
            остаётся только на десктопе. */}
        <div className="hidden lg:block w-full lg:w-[300px] lg:flex-shrink-0">
          <PlanWeekSidebar
            items={plan?.items ?? []}
            phases={phaseSpecs.map((p) => ({ name: p.name, start: p.start, end: p.end }))}
            todayISO={tISO}
            risks={conflictsForBanner.map((c) => ({
              key: c.key,
              severity: c.severity === "alert" ? "danger" : "warning",
              title: c.title,
              summary: c.summary,
            }))}
          />
        </div>
      </div>
      )}
      {daySheet && (
        <PlanDaySheet
          cell={daySheet}
          onClose={() => setDaySheet(null)}
          onSession={onSessionClick}
        />
      )}
      <PlanLoadLandscape
        open={loadOpen}
        onClose={() => setLoadOpen(false)}
        weeks={loadWeeks}
        range={loadRange}
        onRangeChange={setLoadRange}
        loading={loadFetching}
      />
      {picked && (
        <WorkoutActionModal
          wo={picked}
          onClose={() => setPicked(null)}
          variant={typeof window !== "undefined" && window.matchMedia?.("(min-width: 1024px)").matches ? "drawer" : "sheet"}
        />
      )}
      <FABSpeedDial
        icon={Plus}
        label={tFab("fab.plan.label")}
        actions={[
          {
            icon: Plus,
            label: tFab("fab.plan.primary"),
            hint: tFab("fab.plan.primary_hint"),
            onClick: () => nav("/quick-log"),
            testId: "fab-plan-primary",
          },
          ...(canBuildWorkout
            ? [
                {
                  icon: Barbell,
                  label: tFab("fab.plan.build"),
                  hint: tFab("fab.plan.build_hint"),
                  onClick: () => nav("/workout/build"),
                  testId: "fab-plan-build",
                },
              ]
            : []),
          {
            icon: Compass,
            label: tFab("fab.plan.jump_today"),
            hint: tFab("fab.plan.jump_today_hint"),
            onClick: () => setWeekOffset(0),
            testId: "fab-plan-jump-today",
          },
          {
            icon: CalendarPlus,
            label: tFab("fab.plan.copy_week"),
            hint: tFab("fab.plan.copy_week_hint"),
            onClick: () => {
              setCopyWeekNote(null);
              copyWeek.mutate(
                { target_week_start: isoFromDate(monday) },
                {
                  onSuccess: (d) =>
                    setCopyWeekNote(
                      tFab("fab.plan.copy_week_done", { count: d.copied_count }),
                    ),
                  onError: (e: unknown) => {
                    const status = (e as { response?: { status?: number } })?.response?.status;
                    setCopyWeekNote(
                      status === 409
                        ? tFab("fab.plan.copy_week_not_empty")
                        : tFab("fab.plan.copy_week_failed"),
                    );
                  },
                },
              );
            },
            testId: "fab-plan-copy-week",
          },
          {
            icon: NotePencil,
            label: tFab("fab.plan.diary"),
            hint: tFab("fab.plan.diary_hint"),
            onClick: () => nav("/diary"),
            testId: "fab-plan-diary",
          },
        ]}
        testId="fab-plan"
      />
    </div>
  );
}
